/**
 * Tri-state LaunchDarkly flag controlling which validation source feeds the UI.
 * - off: only Agent Validator (PIF compiler) errors are displayed (default).
 * - on: both Agent Validator + Bot Compiler errors are displayed side-by-side.
 * - bot_only: only Bot Compiler errors are displayed; PIF is not invoked (kill switch).
 */
export type AgentCompilerMode = 'off' | 'on' | 'bot_only';

export const AGENT_COMPILER_FLAG_KEY = 'ps-agent-compiler-errors';

// Tolerate common LD variation aliases — case-insensitive, dash/underscore
// interchangeable. Anything unrecognised falls back to 'off'.
export const parseAgentCompilerMode = (value: unknown): AgentCompilerMode => {
  if (typeof value !== 'string') return 'off';
  const normalized = value.trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'on') return 'on';
  if (normalized === 'bot_only' || normalized === 'botonly') return 'bot_only';
  if (normalized === 'off') return 'off';
  return 'off';
};

export const readAgentCompilerMode = (
  featureFlags: unknown
): AgentCompilerMode => {
  if (typeof featureFlags !== 'object' || featureFlags === null) return 'off';
  const raw = (featureFlags as Record<string, unknown>)[
    AGENT_COMPILER_FLAG_KEY
  ];
  return parseAgentCompilerMode(raw);
};

// Bot-compiler calls must be suppressed entirely (not just hidden in the UI)
// when mode is 'off' — in that case only agent/PIF errors should be surfaced.
export const shouldRunBotCompiler = (mode: AgentCompilerMode): boolean =>
  mode === 'on' || mode === 'bot_only';

/**
 * Which compiler the BACKEND should compile with when publishing —
 * `publishSolutionVersion(useBotCompiler:)`, which sets the `CompileWithBotCompiler`
 * Gitea repo variable the publish workflow reads.
 *
 * Deliberately NOT `shouldRunBotCompiler`. That one answers "may we CALL the bot
 * compiler for validation", and it is true for `on` — where the point is to show both
 * error sets side by side while the migration is in flight. Publishing has no such
 * middle: exactly one compiler produces the artifact. Treating `on` as "publish with
 * the bot compiler" would silently move every user on that variation back onto the old
 * compiler, which is the opposite of what the variation is for.
 *
 * So only `bot_only` — the kill switch — flips publishing too, which is what a kill
 * switch has to do to be one. The backend defaults this to `false` (agent compiler)
 * when the argument is omitted, so `off`/`on` match the default and the flag only ever
 * adds the escape hatch.
 */
export const shouldPublishWithBotCompiler = (
  mode: AgentCompilerMode
): boolean => mode === 'bot_only';
